// @ts-check

/**
 * @file Renders the project members route.
 */

import {useLocalSearchParams} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../../src/components/button"
import Card from "../../../src/components/card"
import errorMessage from "../../../src/shared/error-message"
import {Project} from "../../../src/frontend-models/project"
import ProjectUserAssignmentRow from "../../../src/screens/projects/project-user-assignment-row"
import {requiredRouteParam} from "../../../src/shared/route-params"
import ScreenTitle from "../../../src/components/screen-title"
import Text from "@kaspernj/api-maker/build/utils/text"
import {UserProjectLink} from "../../../src/frontend-models/user-project-link"
import UsersSelect from "../../../src/components/users/select"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} ProjectMembersScreenProps */
/** @typedef {{id: string, email: string, name: string}} ProjectMemberUserOption */
/** @typedef {{id: string, user: ProjectMemberUserOption}} ProjectMemberRow */

/**
 * @typedef {object} ProjectMembersScreenState
 * @property {boolean} loading Whether project members are loading.
 * @property {string[]} memberUserIds User ids already linked to the project.
 * @property {string} projectName Loaded project name.
 * @property {ProjectMemberRow[]} rows Current member rows.
 * @property {string} selectedUserId User selected for membership.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Project members manager for a single project.
 * @augments {ShapeComponent<ProjectMembersScreenProps, ProjectMembersScreenState>}
 */
class ProjectMembersScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  projectID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {ProjectMembersScreenState} */
  state = {
    loading: true,
    memberUserIds: [],
    projectName: "",
    rows: [],
    selectedUserId: "",
    status: null
  }
  loadRequestId = 0

  /** Initializes route params and member loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()
    const projectID = requiredRouteParam(params, "id", "Project ID")
    this.params = params
    this.projectID = projectID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadData()
    }, [projectID])
  }

  /**
   * Renders the project members manager.
   * @returns {ReactNode} Project members screen content.
   */
  render() {
    const {
      loading,
      memberUserIds,
      projectName,
      rows,
      selectedUserId,
      status
    } = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="projectMembersScreen"
        />
      )
    }
    const screenTitle = _("Members for %{projectName}", {
      projectName
    })
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="projectMembersScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <Text
          style={styles.title ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="projectMembersScreen/title"
        >
          {screenTitle}
        </Text>
        {status &&
          <Text
            style={styles[`status-${themeName}-${status.kind}`] ||= {
              color: status.kind === "error" ? colors.dangerText : colors.successText,
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 16
            }}
            testID="projectMembersScreen/status"
          >
            {status.text}
          </Text>
        }
        <Card
          style={styles.addRow ||= {
            alignItems: "center",
            flexDirection: "row",
            gap: 8,
            marginBottom: 16
          }}
          testID="projectMembersScreen/addRow"
        >
          <View
            style={styles.selectWrapper ||= {
              flex: 1
            }}
            testID="projectMembersScreen/addRow/userSelect"
          >
            <UsersSelect
              excludeValues={memberUserIds}
              includeEmailInLabel
              onChangeValue={this.tt.onUserSelectChange}
              placeholder={_("Select user")}
              testID="projectMembersScreen/addRow/userSelect/select"
              values={selectedUserId ? [selectedUserId] : []}
            />
          </View>
          <Button
            disabled={!selectedUserId}
            iconName="plus"
            label={_("Add")}
            onPress={this.tt.onAddPress}
            testID="projectMembersScreen/addRow/addButton"
            variant="neutral"
          />
        </Card>
        {rows.length === 0 &&
          <Text
            style={styles[`emptyText-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 15
            }}
            testID="projectMembersScreen/emptyText"
          >
            {_("No members yet")}
          </Text>
        }
        {rows.map((row) =>
          <ProjectUserAssignmentRow
            key={row.id}
            onRemovePress={this.tt.onRemovePress}
            row={row}
            testIDPrefix="projectMembersScreen"
          />
        )}
      </View>
    )
  }

  /**
   * Loads project members.
   * @returns {Promise<void>} Resolves when member state updates.
   */
  loadData = async () => {
    const requestId = ++this.loadRequestId
    const {projectID} = this.tt
    try {
      const project = await Project
        .where({
          id: projectID
        })
        .preload({
          userProjectLinks: {user: true}
        })
        .first()

      if (!project) throw new Error(_("Project not found"))
      if (this.loadRequestId !== requestId) return
      const memberships = project.userProjectLinksLoaded()
      const rows = memberships.map((membership) => this.tt.membershipToRow(membership))
      rows.sort((first, second) =>
        (first.user.name || first.user.email).localeCompare(second.user.name || second.user.email)
      )
      this.s.projectName = project.name() || ""
      this.s.rows = rows
      this.s.memberUserIds = memberships.map((membership) =>
        String(membership.userId())
      )
      this.s.selectedUserId = ""
      this.s.loading = false
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: `${_("Could not load project members")}: ${errorMessage(error)}`
      }
    }
  }

  /**
   * Converts a project membership into row data.
   * @param {UserProjectLink} membership Project membership record.
   * @returns {ProjectMemberRow} Row data.
   */
  membershipToRow(membership) {
    const user = membership.user()
    return {
      id: String(membership.id()),
      user: {
        email: user ? user.email() || "" : "",
        id: String(membership.userId() ?? ""),
        name: user
          ? `${user.firstName() || ""} ${user.lastName() || ""}`.trim()
          : ""
      }
    }
  }

  /**
   * Applies the selected user from the shared user select.
   * @param {(string | number)[] | string | number | undefined} selected Selected value.
   * @returns {void} Nothing.
   */
  onUserSelectChange = (selected) => {
    const selectedUserId = Array.isArray(selected)
      ? selected[0]
        ? String(selected[0])
        : ""
      : selected
        ? String(selected)
        : ""
    this.s.selectedUserId = selectedUserId
  }

  /**
   * Adds the selected user to the project.
   * @returns {Promise<void>} Resolves when add handling completes.
   * @rejects {unknown} When saving fails.
   */
  onAddPress = async () => {
    const {projectID} = this.tt
    const {selectedUserId} = this.s
    if (!selectedUserId) {
      throw new Error("Expected a selected user before adding project member")
    }
    const membership = new UserProjectLink()
    membership.setProjectId(projectID)
    membership.setUserId(selectedUserId)
    try {
      await membership.save()
      this.s.status = {
        kind: "success",
        text: _("User added to project")
      }
      await this.loadData()
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Removes a project member.
   * @param {string} membershipId Membership id to remove.
   * @returns {Promise<void>} Resolves when remove handling completes.
   * @rejects {unknown} When removal fails.
   */
  onRemovePress = async (membershipId) => {
    try {
      const membership = new UserProjectLink()
      membership.setId(membershipId)
      await membership.destroy()
      this.s.status = {
        kind: "success",
        text: _("User removed from project")
      }
      await this.loadData()
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<ProjectMembersScreenProps>} */
const ProjectMembersScreenComponent = memo(shapeComponent(ProjectMembersScreen))

export default ProjectMembersScreenComponent
